/**
 * OnboardingStatus — the self-healing landing for a Microsoft user whose
 * tenant is not yet active in Guardian (Sub-piece C).
 *
 * Supersedes the old `/not-onboarded` dead-end. Reached via the App.tsx
 * 403-on-getCurrentUser redirect. The 403 is the Function App org-allowlist
 * gate; its deny verdict self-heals within ≤1 min of an admin approving the
 * org (see `lib/onboarding-status.ts`). So instead of permanently stranding the
 * admin, this page polls `getCurrentUser()` and forwards into the app the
 * moment access opens: fast at first (to catch a just-approved tenant), then a
 * calm background cadence (so a later approval still auto-forwards), with a
 * manual "Check again".
 *
 * Standalone, NOT wrapped in <Layout>, and rendered outside
 * <AuthenticatedTemplate> (App.tsx top-level route), so it cannot use the
 * authenticated chrome.
 *
 * Carbon (mockup carbon/04, D32): the sign-in frame carries the two states
 * the sign-in mockup draws, relocated here:
 *   not linked  signed in, and the allowlist gate answered 403: the error
 *               InlineNotification ("This account is not linked to a Guardian
 *               organization."), the Microsoft button to sign in with a
 *               different account (account picker), a "Check again" and the
 *               request-access link.
 *               The poll keeps running behind it and forwards on success.
 *   pending     signed out: nothing can be checked without a token, so the
 *               info InlineNotification and the Microsoft button to sign in.
 *   checking    signed in, before the first 403 (or while the gate only fails
 *               transiently): a live "Checking your access" line.
 * Copy stays conceptual: no product or pipeline names (end-user docs rule).
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMsal, useIsAuthenticated } from '@azure/msal-react'
import { api } from '@/lib/api'
import { loginRequest } from '@/lib/auth'
import {
  classifyProbeError,
  nextPollDelayMs,
  shouldShowWaiting,
  FAST_POLL_ATTEMPTS,
  type OnboardingProbe,
} from '@/lib/onboarding-status'
import { Button, InlineNotification, MicrosoftSignInButton, Spinner } from '@/components/carbon'
import { FrameNote, PUBLIC_LINK, SignInFrame } from '@/components/PublicLayout'

/** Which of the three states the page shows. */
export type OnboardingStatusState = 'signed-out' | 'checking' | 'not-linked'

export interface OnboardingStatusViewProps {
  state: OnboardingStatusState
  /** A poll is in flight: "Check again" shows its spinner. */
  checking: boolean
  /** The fast polls are spent: "Check again" appears on the checking state too. */
  waiting: boolean
  /** The signed-in account's sign-in name, when MSAL has one. */
  account?: string
  onCheckAgain: () => void
  onSignIn: () => void
  /** Sign in again with the account picker, to try a different account. */
  onSwitchAccount: () => void
}

/** The page body for one state. Pure, so each state has an SSR render test. */
export function OnboardingStatusView({ state, checking, waiting, account, onCheckAgain, onSignIn, onSwitchAccount }: OnboardingStatusViewProps) {
  const checkAgain = (
    <div className="mt-6">
      <Button kind="tertiary" onClick={onCheckAgain} loading={checking}>
        Check again
      </Button>
    </div>
  )

  return (
    <SignInFrame
      headline={<>Your Microsoft&nbsp;365 tenant, assessed by Guardian.</>}
      lede="Guardian checks your settings against your baseline and the frameworks you follow, then tells you what to close first."
    >
      {state === 'signed-out' ? (
        <>
          <MicrosoftSignInButton onClick={onSignIn} />
          <p className="m-0 mt-2 text-helper text-text-helper">Use your work account. Your password stays with Microsoft.</p>
          <InlineNotification kind="info" title="Your organization’s request is waiting for approval." className="mt-6">
            iuvo reviews every new organization before its first sign-in. You can close this page.
          </InlineNotification>
        </>
      ) : state === 'not-linked' ? (
        <>
          {account ? <p className="m-0 mb-4 text-helper text-text-helper">Signed in as {account}</p> : null}
          <MicrosoftSignInButton onClick={onSwitchAccount} label="Sign in with a different account" />
          <InlineNotification kind="error" title="This account is not linked to a Guardian organization." className="mt-6">
            Ask your iuvo contact to add you, or request access for your organization below.
          </InlineNotification>
          {checkAgain}
          <p className="m-0 mt-2 text-helper text-text-helper">
            This page checks again on its own and opens Guardian once your access is ready.
          </p>
        </>
      ) : (
        <>
          <p role="status" className="m-0 flex items-center gap-3 text-body text-text-primary">
            <Spinner size={20} />
            Checking your access to Guardian.
          </p>
          <p className="m-0 mt-2 text-helper text-text-helper">
            This page opens Guardian on its own once your access is ready. Keep this tab open.
          </p>
          {waiting ? checkAgain : null}
        </>
      )}
      <FrameNote>
        <p className="m-0">
          New to Guardian?{' '}
          <Link to="/signup" className={PUBLIC_LINK}>
            Request access for your organization
          </Link>
        </p>
        <p className="m-0">
          Questions?{' '}
          <a href="mailto:support@iuvotech.com" className={PUBLIC_LINK}>
            support@iuvotech.com
          </a>
        </p>
      </FrameNote>
    </SignInFrame>
  )
}

export default function OnboardingStatus() {
  const navigate = useNavigate()
  const isAuthenticated = useIsAuthenticated()
  const { instance, accounts } = useMsal()

  // `attempts` drives the rendered copy (checking → waiting); attemptsRef is the
  // scheduling source of truth (a ref so the poll closure reads the live count
  // without re-binding). `checking` disables the manual button while a poll is
  // in flight. `probe` is the last failed poll's verdict: 'pending' (the 403
  // allowlist gate) shows the not-linked state, 'transient' keeps checking.
  const [attempts, setAttempts] = useState(0)
  const [checking, setChecking] = useState(true)
  const [probe, setProbe] = useState<Exclude<OnboardingProbe, 'active'> | null>(null)
  const attemptsRef = useRef(0)
  const timerRef = useRef<number | null>(null)
  const cancelledRef = useRef(false)

  const clearTimer = () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }

  const poll = useCallback(async () => {
    if (cancelledRef.current) return
    setChecking(true)
    try {
      await api.getCurrentUser()
      if (cancelledRef.current) return
      // 'active' — the tenant is allowlisted now. Forward into the app. The
      // App-level onboarding gate set its fire-once flag when it redirected
      // here, so it will not re-fire and bounce us back.
      navigate('/', { replace: true })
    } catch (err) {
      if (cancelledRef.current) return
      // 'pending' (403, the allowlist gate) or 'transient' (5xx / network).
      // Both mean "keep polling"; only a 403 says the account is not linked.
      setProbe(classifyProbeError(err))
      setChecking(false)
      attemptsRef.current += 1
      setAttempts(attemptsRef.current)
      clearTimer()
      timerRef.current = window.setTimeout(() => void poll(), nextPollDelayMs(attemptsRef.current))
    }
  }, [navigate])

  useEffect(() => {
    cancelledRef.current = false

    if (isAuthenticated && accounts.length > 0) {
      // Fresh poll loop for the signed-in admin.
      attemptsRef.current = 0
      setAttempts(0)
      setProbe(null)
      setChecking(true)
      void poll()
    } else {
      // Manual / bookmarked visit while signed out: don't poll. Polling would
      // send getCurrentUser() with no token → backend 401 → the response
      // interceptor's loginRedirect, yanking the visitor away. Show the
      // waiting state with the Microsoft button instead.
      clearTimer()
      attemptsRef.current = FAST_POLL_ATTEMPTS
      setAttempts(FAST_POLL_ATTEMPTS)
      setChecking(false)
    }

    return () => {
      cancelledRef.current = true
      clearTimer()
    }
  }, [isAuthenticated, accounts.length, poll])

  const checkAgain = () => {
    clearTimer()
    void poll()
  }

  const signIn = () => {
    instance.loginRedirect(loginRequest).catch(e => {
      console.error('Login failed:', e)
    })
  }

  const switchAccount = () => {
    instance.loginRedirect({ ...loginRequest, prompt: 'select_account' }).catch(e => {
      console.error('Login failed:', e)
    })
  }

  const signedIn = isAuthenticated && accounts.length > 0
  const state: OnboardingStatusState = !signedIn ? 'signed-out' : probe === 'pending' ? 'not-linked' : 'checking'

  return (
    <OnboardingStatusView
      state={state}
      checking={checking}
      waiting={shouldShowWaiting(attempts)}
      account={accounts[0]?.username || undefined}
      onCheckAgain={checkAgain}
      onSignIn={signIn}
      onSwitchAccount={switchAccount}
    />
  )
}
